/**
 * CardinalsOverlay — N/E/S/W cardinal-direction labels around the plan area.
 *
 * Layout:
 *   - A square viewBox inscribed in the plan area (same trick as
 *     SunPathOverlay), so the four labels sit at the midpoints of the four
 *     sides of an inscribed square.
 *   - The whole group is rotated by the apartment azimuth so labels align
 *     with the building orientation; each label is then counter-rotated so
 *     the glyph itself remains upright.
 *
 * Reused by the Insolation mode and (future) the Axonometric mode.
 */

import { useI18n } from '../../../../shared/i18n/useI18n.js'

import { ICON_SIZE, PADDING, VIEW } from '../../constants/overlayDefaults.js'

import styles from './CardinalsOverlay.module.scss'

/**
 * @param {object} props
 * @param {number} [props.azimuthDeg]       defaults to 90 (north up)
 * @param {number} [props.northRotationDeg] extra CW rotation applied to the wrapper (rotate-to-north);
 *   added to the per-label counter-rotation so glyphs stay upright during the CSS transition.
 */
export function CardinalsOverlay({ azimuthDeg, northRotationDeg = 0 }) {

  const { t } = useI18n()

  const CENTER = VIEW / 2
  const CARDINALS = [
    { key: 'N', label: 'N', tooltip: t('directions.compass_direction_north'),   x: CENTER,         y: PADDING        },
    { key: 'E', label: 'E', tooltip: t('directions.compass_direction_east'),   x: VIEW - PADDING, y: CENTER         },
    { key: 'S', label: 'S', tooltip: t('directions.compass_direction_south'), x: CENTER,         y: VIEW - PADDING },
    { key: 'W', label: 'W', tooltip: t('directions.compass_direction_west'),   x: PADDING,        y: CENTER         },
  ]

  const azimuth = Number.isFinite(Number(azimuthDeg)) ? Number(azimuthDeg) : 90
  // SVG `rotate()` is CW-positive; cartesian azimuth is CCW-positive → negate.
  const svgRotationDeg = -(azimuth - 90)

  return (
    <div className={styles.overlay} aria-hidden="true">
      <svg
        viewBox={`0 0 ${VIEW} ${VIEW}`}
        preserveAspectRatio="xMidYMid meet"
        className={styles.svg}
      >
        <g transform={`rotate(${svgRotationDeg} ${CENTER} ${CENTER})`}>
          {CARDINALS.map(({ key, label, tooltip, x, y }) => (
            // Outer <g>: counter-rotates azimuth via SVG attribute — instant, no CSS transition.
            // Keeps the glyph upright when azimuth changes (e.g. Axo frame-by-frame).
            <g key={key} transform={`rotate(${-svgRotationDeg} ${x} ${y})`}>
              {/* Inner text: CSS transition only for northRotationDeg — animates smoothly
                  alongside the wrapper's rotate-to-north CSS transition. */}
              <text
                x={x}
                y={y}
                textAnchor="middle"
                dominantBaseline="middle"
                className={styles.cardinal}
                style={{
                  fontSize: ICON_SIZE,
                  transform: `rotate(${-northRotationDeg}deg)`,
                  transformBox: 'fill-box',
                  transformOrigin: 'center',
                  transition: 'transform 0.4s ease',
                }}
              >
                <title>{tooltip}</title>
                {label}
              </text>
            </g>
          ))}
        </g>
      </svg>
    </div>
  )
}
